import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { hashKey, useMutation, useMutationState, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router";
import type { FeedbackVerdict, FoodFeedbackInput, FoodDataStatus, FoodRecommendation } from "@/shared/contracts";
import { newIdempotencyKey } from "@/shared/api/idempotency";
import { ApiError, toApiError } from "@/shared/api/errors";
import { queryKeys } from "@/shared/query/queryClient";
import { useJourneyReader, type JourneyChoice } from "@/shared/query/journeyReader";
import { feedbackScopeKey, readFoodFeedbackReceipt, reserveFoodFeedbackReceipt, updateFoodFeedbackReceipt, validOriginalFoodFeedback, validFoodFeedbackResult, type FoodFeedbackScope } from "@/shared/query/foodFeedbackReceipt";
import { readChoiceReceipt, reserveChoiceReceipt, updateChoiceReceipt } from "@/shared/query/choiceReceipt";
import { env } from "@/shared/config/env";
import { useServices } from "@/shared/services/registry";
import { useActiveHome, useOptionalCurrentHousehold } from "@/shared/session/householdContext";
import { Button, Card, Chip, DataOriginBadge, ErrorState, Page, QueryView, ToggleChip, TopBar } from "@/shared/ui";
import { recommendationSourceMatches, recommendationMatchesReader, recommendationScopeError } from "./recommendationScope";
import "./food.css";

/** 到店前改去这家；结果不明时只读原操作，不重新提交。 */
function ChooseVenue({ rec }: { rec: FoodRecommendation }) {
  const { visits } = useServices();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const keyRef = useRef(newIdempotencyKey("visit-choice"));
  const currentRecommendation = useRef(rec.recommendation_id);
  currentRecommendation.current = rec.recommendation_id;
  const currentRec = useRef(rec); currentRec.current = rec;
  const home = useActiveHome();
  const reader = useJourneyReader({ home, enabled: rec.mode === "pet_virtual_explore", scenario: env.dataMode === "fixture" ? "train" : undefined });
  type Operation = { state: "pending" | "unknown" | "in_progress" | "received"; capture: JourneyChoice; result?: unknown; lookupError?: unknown; lookupBlocked?: boolean; applied?: boolean };
  const operationKey = ["visit-choice", ...reader.key, rec.recommendation_id];
  const receiptKey = (capture: JourneyChoice, recommendationId = rec.recommendation_id) => ["visit-choice", ...capture.queryKey, recommendationId];
  const heldOperations = useMutationState({ filters: { mutationKey: operationKey, exact: true }, select: (mutation) => mutation.state.status });
  const operation = useQuery<Operation>({ queryKey: operationKey, queryFn: async () => { throw new Error("Local operation receipt only"); }, enabled: false, gcTime: Infinity });
  const [storageFailure, setStorageFailure] = useState<string | null>(null);
  const [, refreshReceipt] = useState(0);
  const persisted = reader.scope ? readChoiceReceipt(reader.scope, rec.recommendation_id, reader.readCurrent()?.planned_visit_id ?? undefined) : null;
  const storageBlocked = storageFailure === reader.fingerprint || persisted?.kind === "error";
  const restoredCapture = persisted?.kind === "held" ? reader.bindOriginalChoice({
    scope: persisted.receipt.scope, queryKey: reader.key, epoch: -1,
    plannedVisitId: persisted.receipt.plannedVisitId, version: persisted.receipt.expectedVersion, idempotencyKey: persisted.receipt.idempotencyKey,
  }) : null; // -1 is not authority; only the current reader may issue a new epoch.
  const restored: Operation | undefined = restoredCapture && persisted?.kind === "held" ? {
    capture: restoredCapture, state: persisted.receipt.state === "received" ? "received" : "unknown", lookupBlocked: persisted.receipt.state === "blocked",
  } : undefined;
  const heldOperation = operation.data ?? restored;
  const currentOperation = heldOperation && persisted?.kind === "held" ? persisted.receipt.state === "received" ? { ...heldOperation, state: "received" as const, lookupBlocked: true } : persisted.receipt.state === "blocked" ? { ...heldOperation, lookupBlocked: true } : heldOperation : heldOperation;
  const persistState = (capture: JourneyChoice, state: "unknown" | "blocked" | "received", recommendationId = rec.recommendation_id) => {
    if (!updateChoiceReceipt(capture, recommendationId, state)) setStorageFailure(hashKey(capture.queryKey));
  };
  type Submission = { capture: JourneyChoice; recommendationId: string };
  const choose = useMutation({
    mutationKey: operationKey, gcTime: Infinity, retry: false,
    onMutate: ({ capture, recommendationId }: Submission) => queryClient.setQueryData(receiptKey(capture, recommendationId), { state: "pending", capture }),
    onError: (error, { capture, recommendationId }) => {
      const e = toApiError(error);
      const lookupBlocked = e.isAuth || e.isCapabilityUnavailable || (e.kind === "http" && typeof e.status === "number" && e.status >= 400 && e.status < 500 && e.code !== "IDEMPOTENCY_IN_PROGRESS");
      persistState(capture, lookupBlocked ? "blocked" : "unknown", recommendationId);
      queryClient.setQueryData(receiptKey(capture, recommendationId), { state: "unknown", capture, result: error, lookupBlocked });
    },
    mutationFn: async ({ capture, recommendationId }: Submission) => {
      const current = reader.captureChoice(capture.idempotencyKey);
      if (currentRecommendation.current !== recommendationId || !current || current.epoch !== capture.epoch || current.version !== capture.version || current.plannedVisitId !== capture.plannedVisitId || rec.freshness !== "fresh" || rec.itinerary_version !== capture.version || !recommendationMatchesReader(currentRec.current, reader.readCurrent(), env.dataMode === "fixture")) throw new ApiError({ kind: "http", status: 409, code: "VERSION_CONFLICT", message: "行程已经变了，请重新查看。" });
      const saved = readChoiceReceipt(capture.scope, recommendationId);
      if (saved.kind !== "held" || saved.receipt.state !== "pending" || saved.receipt.idempotencyKey !== capture.idempotencyKey) throw ApiError.capability("journey.visit", "这次选择暂时无法安全保存，请先返回查看行程。");
      return visits.choose(capture.plannedVisitId, { recommendation_id: recommendationId, expected_itinerary_version: capture.version }, capture.idempotencyKey);
    },
    onSuccess: (snapshot, { capture, recommendationId }) => {
      if (snapshot.pet_id !== capture.scope.petId || (capture.scope.mode === "live" && snapshot.journey_id !== capture.scope.journeyId)
        || !["active", "completed", "cancelled"].includes(snapshot.lifecycle) || !Number.isSafeInteger(snapshot.itinerary_version) || snapshot.itinerary_version < capture.version
        || (snapshot.planned_visit_id !== capture.plannedVisitId && snapshot.current_visit_id !== capture.plannedVisitId)) {
        persistState(capture, "blocked", recommendationId);
        queryClient.setQueryData(receiptKey(capture, recommendationId), { state: "unknown", capture, lookupBlocked: true });
        return;
      }
      persistState(capture, "received", recommendationId);
      queryClient.setQueryData(receiptKey(capture, recommendationId), { state: "received", capture, result: snapshot });
      if (active.current && currentRecommendation.current === recommendationId && recommendationMatchesReader(currentRec.current, reader.readCurrent(), env.dataMode === "fixture") && reader.commitChoice(snapshot, capture)) navigate("/journey");
    },
  });
  const active = useRef(true);
  const inFlight = useRef<AbortController | null>(null);
  useEffect(() => {
    active.current = true;
    return () => { active.current = false; inFlight.current?.abort(); inFlight.current = null; };
  }, [reader.fingerprint, rec.recommendation_id]);
  type Lookup = { recommendationId: string; held: Operation; projectionCapture: JourneyChoice; controller: AbortController; stillCurrent: () => boolean };
  const lookup = useMutation({
    retry: false,
    mutationFn: async (request: Lookup) => {
      if (!request.stillCurrent()) throw new ApiError({ kind: "aborted", code: "ABORTED", message: "读取已结束。" });
      if (typeof visits.choiceResult !== "function") throw ApiError.capability("journey.visit", "上次选择结果暂时还不能查看。");
      return visits.choiceResult(request.held.capture.plannedVisitId, request.held.capture.idempotencyKey, request.controller.signal);
    },
    onSuccess: (result, request) => {
      if (!request.stillCurrent()) return;
      const held = request.held, capture = held.capture, snapshot = result?.snapshot;
      if ((result?.state === "unknown" || result?.state === "in_progress") && snapshot == null) {
        persistState(capture, "unknown", request.recommendationId);
        queryClient.setQueryData(receiptKey(capture, request.recommendationId), { ...held, state: result.state, result: undefined, lookupError: undefined });
        return;
      }
      // An old completed receipt confirms only that operation. It may not replace newer facts.
      const valid = result?.state === "completed" && snapshot && snapshot.pet_id === capture.scope.petId
        && (capture.scope.mode === "fixture" || snapshot.journey_id === capture.scope.journeyId)
        && ["active", "completed", "cancelled"].includes(snapshot.lifecycle)
        && Number.isSafeInteger(snapshot.itinerary_version) && snapshot.itinerary_version >= capture.version
        && (snapshot.planned_visit_id === capture.plannedVisitId || snapshot.current_visit_id === capture.plannedVisitId);
      if (!valid || !snapshot) {
        persistState(capture, "blocked", request.recommendationId);
        queryClient.setQueryData(receiptKey(capture, request.recommendationId), { ...held, state: "unknown", lookupError: new ApiError({ kind: "http", status: 409, code: "VERSION_CONFLICT", message: "这次结果还无法确认，先不重复提交。" }), lookupBlocked: true });
        return;
      }
      persistState(capture, "received", request.recommendationId);
      const applied = recommendationMatchesReader(currentRec.current, reader.readCurrent(), env.dataMode === "fixture") && reader.commitChoice(snapshot, request.projectionCapture);
      queryClient.setQueryData(receiptKey(capture, request.recommendationId), { state: "received", capture, result: snapshot, applied });
      if (applied) navigate("/journey");
    },
    onError: (error, request) => {
      if (!request.stillCurrent()) return;
      const e = toApiError(error);
      const mayReadAgain = (e.kind === "network" || e.kind === "timeout") && e.retryable && !e.fromException;
      persistState(request.held.capture, mayReadAgain ? "unknown" : "blocked", request.recommendationId);
      queryClient.setQueryData(receiptKey(request.held.capture, request.recommendationId), { ...request.held, lookupError: error, lookupBlocked: !mayReadAgain });
    },
    onSettled: (_result, _error, request) => { if (inFlight.current === request.controller) inFlight.current = null; },
  });
  const readOriginal = () => {
    const currentReceipt = reader.scope ? readChoiceReceipt(reader.scope, rec.recommendation_id) : null;
    if (storageBlocked || currentReceipt?.kind === "error") { setStorageFailure(reader.fingerprint); return; }
    const original = currentOperation;
    if (!original || currentReceipt?.kind !== "held" || currentReceipt.receipt.state === "blocked" || currentReceipt.receipt.state === "received" || currentReceipt.receipt.idempotencyKey !== original.capture.idempotencyKey) { refreshReceipt((n) => n + 1); return; }
    const held = original;
    if (!held || !["unknown", "in_progress"].includes(held.state) || held.lookupBlocked || inFlight.current || !reader.readCurrent() || hashKey(held.capture.queryKey) !== reader.fingerprint) return;
    if (!updateChoiceReceipt(held.capture, rec.recommendation_id, currentReceipt.receipt.state)) { setStorageFailure(reader.fingerprint); return; }
    const projectionCapture = reader.bindOriginalChoice(held.capture);
    if (!projectionCapture) return;
    const controller = new AbortController(); inFlight.current = controller;
    // This closure binds the request epoch even if A -> B -> A occurs before the response.
    const recommendationId = rec.recommendation_id;
    const stillCurrent = () => currentRecommendation.current === recommendationId && active.current && !controller.signal.aborted && Boolean(reader.readCurrent()) && hashKey(held.capture.queryKey) === reader.fingerprint;
    lookup.mutate({ recommendationId, held, projectionCapture, controller, stillCurrent });
  };
  const snapshot = reader.details.status === "aligned" ? reader.details.snapshot : null;
  if (rec.mode !== "pet_virtual_explore") return null;
  if (reader.details.status === "failed") return <ErrorState error={reader.details.error} onRetry={reader.details.refresh ?? undefined} />;
  if (snapshot && storageBlocked) return <Card flat className="ps-stack"><p role="alert">这次选择的记录暂时无法安全读取或保存，已停止提交。请先返回查看行程，暂时不要重复选择。</p></Card>;
  if (snapshot && persisted?.kind === "other") return <Card flat className="ps-stack"><p role="status">这段行程已有一次选择，请返回原推荐查看，暂时不要重复选择。</p></Card>;
  // Keep the receipt reachable after the recommendation becomes stale or the visit progresses.
  if (currentOperation && snapshot) return (
    <Card flat className="ps-stack">
      <p role="status" className="ps-muted">{currentOperation.state === "received" ? "上次选择已完成，当前行程保持不变。" : currentOperation.state === "in_progress" ? "上次选择还在处理中，先不重复提交。" : currentOperation.state === "pending" ? "正在提交这次选择…" : "上次选择还没有确认，先不重复提交。"}</p>
      {currentOperation.state === "unknown" || currentOperation.state === "in_progress" ? <Button variant="secondary" icon="refresh" loading={lookup.isPending} disabled={Boolean(currentOperation.lookupBlocked)} onClick={readOriginal}>查看上次选择结果</Button> : null}
      {currentOperation.state === "unknown" && currentOperation.result instanceof Error && !currentOperation.lookupError ? <ErrorState error={currentOperation.result} /> : null}
      {currentOperation.state !== "received" && currentOperation.lookupBlocked && !currentOperation.result && !currentOperation.lookupError ? <p className="ps-muted">上次选择暂时不能继续核对，请先返回查看行程。</p> : null}
      {currentOperation.lookupError ? <ErrorState error={currentOperation.lookupError} /> : null}
    </Card>
  );
  if (!recommendationMatchesReader(rec, snapshot, env.dataMode === "fixture")) return <ErrorState error={recommendationScopeError()} />;
  if (!snapshot || !snapshot.planned_visit_id) return null;
  if (snapshot.current_visit_id) return <Chip>TA 已经到店了，这次不能再换</Chip>;
  if (rec.freshness !== "fresh" || rec.itinerary_version !== snapshot.itinerary_version) return <Chip tone="danger">行程已经变了，这条推荐需要重新查看</Chip>;
  const error = choose.error ? toApiError(choose.error) : null;
  return (
    <Card flat className="ps-stack">
      <Button variant="primary" icon="pin" loading={choose.isPending} disabled={choose.isError || Boolean(currentOperation) || heldOperations.length > 0} onClick={() => {
        const capture = reader.captureChoice(keyRef.current);
        if (capture && recommendationMatchesReader(currentRec.current, reader.readCurrent(), env.dataMode === "fixture") && !choose.isPending && !choose.isError && !currentOperation && heldOperations.length === 0) {
          const reserved = reserveChoiceReceipt(capture, rec.recommendation_id);
          if (reserved === "error") setStorageFailure(reader.fingerprint);
          else if (reserved === "held") refreshReceipt((n) => n + 1);
          else choose.mutate({ capture, recommendationId: rec.recommendation_id });
        }
      }}>让 TA 改去这家</Button>
      <span className="ps-muted">交通不会为了餐厅缩短；店内是原创场景，不代表真实店内样子。</span>
      {error ? <ErrorState error={error} /> : null}
    </Card>
  );
}

const REASONS: Array<{ id: string; label: string }> = [
  { id: "too_salty", label: "偏咸" },
  { id: "too_bland", label: "偏淡" },
  { id: "too_spicy", label: "太辣" },
  { id: "too_oily", label: "太油" },
  { id: "too_sweet", label: "太甜" },
];

/** 主人现实用餐反馈：自报，只修正口味偏好，不回流为品质证据。 */
export function OwnerFeedback({ rec }: { rec: FoodRecommendation }) {
  const context = useOptionalCurrentHousehold();
  const fixture = env.dataMode === "fixture";
  const authorized = Boolean(context?.userId && context.household?.household_id && context.pet?.pet_id === rec.pet_id
    && context.household.pets.some(p => p.pet_id === rec.pet_id && p.join_step === "moved_in"));
  const source = rec.mode === "owner_real_dining" && rec.journey_id === null && typeof rec.pet_id === "string" && Boolean(rec.pet_id.trim())
    && typeof rec.recommendation_id === "string" && Boolean(rec.recommendation_id.trim()) && rec.data_origin === (fixture ? "fixture" : "live");
  const scope: FoodFeedbackScope | null = source && (fixture ? !context?.pet || context.pet.pet_id === rec.pet_id : authorized) ? {
    mode: fixture ? "fixture" : "live", userId: context?.userId ?? "fixture", householdId: context?.household?.household_id ?? "fixture",
    petId: rec.pet_id!, recommendationId: rec.recommendation_id,
  } : null;
  const fingerprint = scope ? feedbackScopeKey(scope) : "unavailable";
  const authority = useRef({ fingerprint, epoch: 0, active: true });
  if (authority.current.fingerprint !== fingerprint) authority.current = { fingerprint, epoch: authority.current.epoch + 1, active: true };
  const epoch = authority.current.epoch;
  useLayoutEffect(() => { authority.current.active = true; return () => { authority.current.active = false; }; }, []);
  const current = () => authority.current.active && authority.current.fingerprint === fingerprint && authority.current.epoch === epoch;
  if (rec.mode !== "owner_real_dining") return null;
  if (!scope) return <Card flat><p role="alert">现在无法确认这条反馈的归属，请返回确认当前家庭和推荐。</p></Card>;
  return <ScopedOwnerFeedback key={fingerprint} scope={scope} rec={rec} isCurrent={current} />;
}

function ScopedOwnerFeedback({ scope, rec, isCurrent }: { scope: FoodFeedbackScope; rec: FoodRecommendation; isCurrent: () => boolean }) {
  const { food } = useServices();
  const queryClient = useQueryClient();
  const [verdict, setVerdict] = useState<FeedbackVerdict | null>(null);
  const [reasons, setReasons] = useState<string[]>([]);
  const [storageFailure, setStorageFailure] = useState(false);
  const [, refreshReceipt] = useState(0);
  const active = useRef(true);
  useLayoutEffect(() => { active.current = true; return () => { active.current = false; }; }, []);
  const current = () => active.current && isCurrent();
  const key = ["food", "feedback-scoped", scope.mode, scope.userId, scope.householdId, scope.petId, scope.recommendationId];
  const receipt = readFoodFeedbackReceipt(scope);
  const blocked = storageFailure || receipt.kind !== "empty";
  const invalid = () => new ApiError({ kind: "http", status: 409, code: "VERSION_CONFLICT", retryable: false, message: "这条反馈的归属或结果无法确认，请先返回查看。" });
  const confirm = (idempotencyKey: string) => {
    if (!current()) return false;
    const before = readFoodFeedbackReceipt(scope);
    if (before.kind === "held" && before.receipt.state === "blocked") return false;
    if (!updateFoodFeedbackReceipt(scope, idempotencyKey, "confirmed")) { setStorageFailure(true); return false; }
    refreshReceipt(n => n + 1);
    // Only this confirmed current principal/household/pet owner's preference can be invalidated.
    void queryClient.invalidateQueries({ queryKey: ["food", "preference-scoped", scope.mode, scope.userId, scope.householdId, scope.petId, "owner"] });
    return true;
  };
  const fail = (error: unknown, idempotencyKey: string) => {
    if (!current()) return;
    const e = toApiError(error);
    const stopped = e.isAuth || e.isCapabilityUnavailable || (e.kind === "http" && e.status !== null && e.status >= 400 && e.status < 500 && e.code !== "IDEMPOTENCY_IN_PROGRESS");
    if (!updateFoodFeedbackReceipt(scope, idempotencyKey, stopped ? "blocked" : "unknown")) setStorageFailure(true);
    refreshReceipt(n => n + 1);
  };
  type Submission = { body: FoodFeedbackInput; idempotencyKey: string };
  const submit = useMutation({
    mutationKey: [...key, "submit"], retry: false, networkMode: "always",
    mutationFn: async ({ body, idempotencyKey }: Submission) => {
      const before = readFoodFeedbackReceipt(scope);
      if (!current() || before.kind !== "held" || before.receipt.state !== "pending" || before.receipt.idempotencyKey !== idempotencyKey || body.recommendation_id !== scope.recommendationId) throw invalid();
      const result = await food.feedback(body, idempotencyKey);
      if (!current()) return null;
      if (!validOriginalFoodFeedback(result, scope)) throw invalid();
      confirm(idempotencyKey);
      return null; // Private response stays out of mutation result data.
    },
    onError: (error, { idempotencyKey }) => fail(error, idempotencyKey),
  });
  const lookup = useMutation({
    mutationKey: [...key, "original-result"], retry: false, networkMode: "always",
    mutationFn: async (idempotencyKey: string) => {
      const before = readFoodFeedbackReceipt(scope);
      if (!current() || before.kind !== "held" || !["pending", "unknown"].includes(before.receipt.state) || before.receipt.idempotencyKey !== idempotencyKey) return null;
      if (!food.feedbackResult) throw ApiError.capability("food.feedback", "这里暂时还不能核对上次反馈。");
      const result = await food.feedbackResult(scope.recommendationId, scope.petId, idempotencyKey);
      if (!current()) return null;
      const after = readFoodFeedbackReceipt(scope);
      if (after.kind !== "held" || !["pending", "unknown"].includes(after.receipt.state) || after.receipt.idempotencyKey !== idempotencyKey) return null;
      if (!validFoodFeedbackResult(result, scope)) throw invalid();
      if (result.state === "completed") confirm(idempotencyKey);
      return result.state; // State only, never a feedback payload or current preference.
    },
    onError: (error, idempotencyKey) => fail(error, idempotencyKey),
  });
  if (receipt.kind === "held" && receipt.receipt.state === "confirmed" && !storageFailure) return <Card flat role="status">
    {scope.mode === "fixture" ? <DataOriginBadge origin="fixture" label="演示反馈，只在本地预览" /> : null}
    谢谢！已按你的感受微调“我的用餐偏好”（自报反馈不会当作餐厅品质证据）。
  </Card>;
  const notice = storageFailure || receipt.kind === "error" ? "这次反馈暂时无法安全记录，已停止提交和核对。"
    : receipt.kind === "held" ? "这次反馈还没有确认，请先查询上次反馈结果，不要重复提交。" : null;
  return (
    <Card flat className="ps-stack">
      <strong>吃过了？说说感受</strong>
      {scope.mode === "fixture" ? <DataOriginBadge origin="fixture" label="演示反馈，只在本地预览" /> : null}
      {notice ? <p role="status">{notice}</p> : null}
      {receipt.kind === "held" && ["pending", "unknown"].includes(receipt.receipt.state) && !storageFailure ? <Button variant="ghost" size="sm" loading={lookup.isPending} onClick={() => {
        if (current() && !lookup.isPending) lookup.mutate(receipt.receipt.idempotencyKey);
      }}>查询上次反馈结果</Button> : null}
      {lookup.data === "unknown" ? <p role="status">暂时还查不到上次反馈的结果，请先不要重复提交。</p> : null}
      {lookup.data === "in_progress" ? <p role="status">上次反馈还在处理中，请稍后手动查询。</p> : null}
      <fieldset className="ps-stack" disabled={blocked || submit.isPending} style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>
      <div className="ps-segmented" role="group" aria-label="总体感受">
        {(["liked", "neutral", "disliked"] as FeedbackVerdict[]).map((v) => (
          <ToggleChip key={v} pressed={verdict === v} onToggle={() => setVerdict(v)}>
            {v === "liked" ? "喜欢" : v === "neutral" ? "一般" : "不喜欢"}
          </ToggleChip>
        ))}
      </div>
      <div className="ps-segmented" role="group" aria-label="口味问题">
        {REASONS.map((r) => (
          <ToggleChip key={r.id} pressed={reasons.includes(r.id)} onToggle={() => setReasons(prev => prev.includes(r.id) ? prev.filter(x => x !== r.id) : [...prev, r.id])}>
            {r.label}
          </ToggleChip>
        ))}
      </div>
      <Button variant="secondary" disabled={!verdict || blocked} loading={submit.isPending} onClick={() => {
        if (!current() || blocked || submit.isPending || !verdict) return;
        const idempotencyKey = newIdempotencyKey("food-feedback");
        const reserved = reserveFoodFeedbackReceipt(scope, idempotencyKey);
        if (reserved !== "reserved") { if (reserved === "error") setStorageFailure(true); else refreshReceipt(n => n + 1); return; }
        const date = new Date();
        submit.mutate({ body: { recommendation_id: scope.recommendationId, dish_ids: rec.dishes.map(d => d.dish.dish_id), verdict, reasons, dined_on: `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}` }, idempotencyKey });
      }}>提交</Button>
      </fieldset>
      {submit.isError ? <ErrorState error={submit.error} /> : null}
      {lookup.isError ? <ErrorState error={lookup.error} /> : null}
    </Card>
  );
}

/**
 * 资料来源这张卡：只写人话（资料是演示的还是核实过的、什么时候整理的、覆盖多大范围）。
 * 资料 / 规则 / 偏好的版本号只用于后台判断推荐是否过期，不给玩家看；状态代码（fixture 等）换成中文，认不出的状态整行不显示。
 */
const DATA_STATUS_TEXT: Partial<Record<FoodDataStatus, string>> = {
  fixture: "演示资料",
  live_verified: "已核实的资料",
  live_partial: "只有一部分资料核实过",
};

/** 看的人所在时区的“几月几日 几点”；没有时间、或认不出来时返回 null（整行不显示，不写“未知”）。 */
function whenText(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat("zh-CN", { month: "long", day: "numeric", hour: "2-digit", minute: "2-digit", hour12: false }).format(date);
}

function SourceCard({ rec }: { rec: FoodRecommendation }) {
  const status = DATA_STATUS_TEXT[rec.provenance.data_status] ?? null;
  const generated = whenText(rec.provenance.generated_at);
  return (
    <Card flat>
      <div className="ps-section-title" style={{ marginTop: 0 }}>资料来源</div>
      <div className="ps-muted">{rec.provenance.coverage_note}</div>
      {/* 状态和整理时间各占一行：挤在一行时 320 宽会把“23:49”或末尾的“·”单独折下去 */}
      {status ? <div className="ps-muted">资料状态：{status}</div> : null}
      {generated ? <div className="ps-muted">整理于 {generated}</div> : null}
      <div className="ps-muted">平台评分：{rec.branch.ratings.length ? "见上" : rec.provenance.data_status === "fixture" ? "无（演示资料没有平台评分）" : "没有"}</div>
    </Card>
  );
}

/** 寻味详情：出处、日期、覆盖范围、实际样本量与平台总数分开展示。推荐 ID 不等于到访。 */
export function RecommendationDetailPage() {
  const { recommendationId = "" } = useParams();
  const { food } = useServices();
  const context = useOptionalCurrentHousehold();
  const home = useActiveHome();
  const fixture = env.dataMode === "fixture";
  const petId = home.data?.pet.pet_id;
  const authorized = Boolean(petId && !home.isPending && !home.isError && (fixture || (context?.userId && context.household && context.pet?.pet_id === petId)));
  const scope = [fixture ? "fixture" : "reader", context?.userId, context?.household?.household_id, petId, home.data?.journey?.journey_id ?? null, home.data?.journey?.itinerary_version ?? null];
  const query = useQuery({ queryKey: [...queryKeys.foodRecommendation(recommendationId), ...scope], queryFn: async ({ signal }) => {
    const rec = await food.recommendation(recommendationId);
    if (signal.aborted) throw new ApiError({ kind: "aborted", code: "ABORTED", message: "读取已结束。" });
    if (rec.recommendation_id !== recommendationId || !petId || rec.pet_id !== petId || (fixture && rec.data_origin !== "fixture")
      || (rec.mode === "owner_real_dining" && rec.journey_id !== null)) throw recommendationScopeError();
    return rec;
  }, enabled: authorized, retry: false, refetchOnMount: false, refetchOnWindowFocus: false, refetchOnReconnect: false });
  const reader = useJourneyReader({ home, enabled: authorized && query.data?.mode === "pet_virtual_explore", scenario: fixture ? "train" : undefined });
  const present = reader.details.status === "aligned" ? reader.details.snapshot : null;

  return (
    <Page>
      <TopBar title="为什么选这里" back="/journey/food" />
      <QueryView query={query}>
        {(rec) => !authorized ? <ErrorState error={recommendationScopeError()} />
          : !recommendationSourceMatches(rec, petId, rec.mode === "pet_virtual_explore" ? present?.journey_id : null, fixture)
            ? reader.details.status === "pending" ? <p role="status">正在核对这条推荐的行程…</p> : <ErrorState error={recommendationScopeError()} />
          : rec.mode === "pet_virtual_explore" && !recommendationMatchesReader(rec, present, fixture)
            ? <div className="ps-stack"><ErrorState error={recommendationScopeError()} /><ChooseVenue rec={rec} /></div>
          : (
          <div className="ps-stack">
            <Card>
              <h2 className="ps-h2">{rec.branch.name}</h2>
              {/* 分店编号和内部规则不上页面；资料里有品牌才写品牌 */}
              {rec.branch.brand ? <div className="ps-muted">品牌：{rec.branch.brand}</div> : null}
              <DataOriginBadge origin={rec.data_origin} label="演示资料，不对应真实商家" />
            </Card>
            <ChooseVenue rec={rec} />
            {rec.dishes.map(({ dish, why, evidence }) => (
              <Card key={dish.dish_id}>
                <strong>{dish.name}</strong>
                <div className="ps-muted">{why}</div>
                {dish.price ? <Chip>{(dish.price.amount_minor / 100).toFixed(0)} {dish.price.currency}（示例价格，日期未知）</Chip> : <Chip>价格未知</Chip>}
                <div className="ps-section-title">依据</div>
                {evidence.length === 0 ? <p className="ps-muted">没有可引用的菜品证据。</p> : null}
                {evidence.map((ev) => {
                  const observed = whenText(ev.observed_at);
                  return (
                    <div key={ev.evidence_id} className="ps-evidence">
                      <Chip>{ev.source_label}</Chip> {ev.aspect}：{ev.observation}
                      <div className="ps-muted">实际取得样本 {ev.sample_count} 条</div>
                      {observed ? <div className="ps-muted">观察时间 {observed}</div> : null}
                    </div>
                  );
                })}
              </Card>
            ))}
            <OwnerFeedback rec={rec} />
            <SourceCard rec={rec} />
          </div>
        )}
      </QueryView>
    </Page>
  );
}
